/**
 * The hourly board, as `<DataTable>` columns. See `design-notes.md`.
 *
 * Four columns: the line and its MO, the hourly plan, one bar cell per hour of the day, and the
 * day so far against the day plan. Every colour comes from a category the server assigned
 * (`cell_status`, `pace_status`, `hour_status`) — this file maps a category to a token and does
 * nothing else. **No threshold lives here**; they are in the report's SQL (§5.2).
 *
 * Bar heights are chart geometry, not measures: an output and a plan that both arrived from SQL,
 * expressed as a length. Inline `style` carries those lengths and nothing else — §4.8's one
 * exception, the same way the sewing floor board draws its efficiency bar.
 */

import type { ColumnDef } from '@tanstack/react-table';

import { cn } from '@/lib/utils';
import { STATUS_TONES } from '@/reports/sewing-line-status';

import { CELL_STATUS_LABEL, cellStatus, cellTone, isPending, type CellStatus } from './status';
import type { CellIndex, HourRow, LineRow } from './grid';

function num(row: Record<string, unknown> | undefined, field: string): number | null {
  if (row === undefined) return null;
  const value = row[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(row: Record<string, unknown> | undefined, field: string): string {
  if (row === undefined) return '';
  const value = row[field];
  return typeof value === 'string' ? value : '';
}

const integer = new Intl.NumberFormat();

export function count(value: number | null): string {
  return value === null ? '—' : integer.format(Math.round(value));
}

export function wholePercent(value: number | null): string {
  return value === null ? '—' : `${integer.format(Math.round(value))}%`;
}

/** Signed, so a floor that is ahead reads as ahead rather than as a smaller shortfall. */
export function signedCount(value: number | null): string {
  if (value === null) return '—';
  const rounded = Math.round(value);
  return `${rounded >= 0 ? '+' : '−'}${integer.format(Math.abs(rounded))}`;
}

/**
 * The bar occupies the lower part of the cell so the figure sits above it rather than on it — a
 * number printed over a coloured fill is the one thing that stops being readable across a floor.
 */
const BAR_MAX_PCT = 58;

/**
 * Track length for one hour's bar: a quarter above the hourly plan, so a line that beats its plan
 * still shows headroom and the plan tick never sits flush against the top. An hour that beat even
 * that extends the track to itself rather than overflowing the cell.
 */
function cellScale(output: number, plan: number): number {
  return Math.max(plan * 1.25, output, 1);
}

function heightPct(value: number, scale: number): string {
  return `${String(Math.max(0, Math.min(BAR_MAX_PCT, (value / scale) * BAR_MAX_PCT)))}%`;
}

/** Bar geometry, clamped to the track. A line over 100% fills it rather than overflowing. */
function widthPct(value: number | null): string {
  return `${String(Math.max(0, Math.min(100, value ?? 0)))}%`;
}

interface Scale {
  cell: string;
  ctq: string;
  style: string;
  header: string;
  lineId: string;
  mo: string;
  planHr: string;
  hourHead: string;
  hourTrack: string;
  hourFigure: string;
  dayTrack: string;
  dayValue: string;
  dayPlan: string;
  footLabel: string;
  footFigure: string;
  footPct: string;
  legend: string;
  wLine: string;
  wPlan: string;
  wDay: string;
}

/**
 * The board's densities; `useFittedTier` measures the available height and picks one.
 * Discrete prebuilt class sets, never a computed font size — §4.8 allows runtime `style` for
 * geometry only.
 */
export type Tier = 'desk' | 'sm' | 'md' | 'lg' | 'xl';

const SCALE: Record<Tier, Scale> = {
  // ~68px rows — a floor of a dozen lines on a 1080p screen, the reference's own density.
  xl: {
    cell: 'py-2 px-3 align-middle',
    header: 'py-2 px-3 text-base font-semibold tracking-widest uppercase',
    lineId: 'text-4xl font-bold leading-none',
    mo: 'text-lg',
    planHr: 'text-3xl font-bold',
    hourHead: 'text-base',
    hourTrack: 'h-14 rounded-lg',
    ctq: 'text-sm px-2 py-0.5',
    style: 'text-lg',
    hourFigure: 'text-2xl',
    dayTrack: 'h-6 rounded-full',
    dayValue: 'w-24 text-4xl font-bold',
    dayPlan: 'w-24 text-lg',
    footLabel: 'text-base tracking-widest',
    footFigure: 'text-2xl',
    footPct: 'text-base',
    legend: 'px-3 py-2.5 text-base',
    wLine: 'w-[26rem] max-w-[26rem]',
    wPlan: 'w-28 max-w-28',
    wDay: 'w-[26rem] max-w-[26rem]',
  },
  // ~44px rows.
  lg: {
    cell: 'py-1.5 px-3 align-middle',
    header: 'py-1.5 px-3 text-sm font-semibold tracking-widest uppercase',
    lineId: 'text-2xl font-bold leading-none',
    mo: 'text-base',
    planHr: 'text-2xl font-bold',
    hourHead: 'text-sm',
    hourTrack: 'h-12 rounded-md',
    ctq: 'text-xs px-2 py-0.5',
    style: 'text-base',
    hourFigure: 'text-xl',
    dayTrack: 'h-5 rounded-full',
    dayValue: 'w-20 text-2xl font-bold',
    dayPlan: 'w-20 text-base',
    footLabel: 'text-sm tracking-widest',
    footFigure: 'text-xl',
    footPct: 'text-sm',
    legend: 'px-3 py-2 text-sm',
    wLine: 'w-96 max-w-96',
    wPlan: 'w-24 max-w-24',
    wDay: 'w-96 max-w-96',
  },
  // ~29px rows — thirty lines on one screen, which BVN K4 needs.
  md: {
    cell: 'py-1 px-2 align-middle',
    header: 'py-1 px-2 text-sm font-semibold tracking-widest uppercase',
    lineId: 'text-lg font-bold leading-none',
    mo: 'text-sm',
    planHr: 'text-lg font-bold',
    hourHead: 'text-xs',
    hourTrack: 'h-9 rounded-sm',
    ctq: 'text-xs px-1.5 py-0.5',
    style: 'text-sm',
    hourFigure: 'text-base',
    dayTrack: 'h-4 rounded-full',
    dayValue: 'w-16 text-lg font-bold',
    dayPlan: 'w-16 text-sm',
    footLabel: 'text-xs tracking-widest',
    footFigure: 'text-base',
    footPct: 'text-xs',
    legend: 'px-2 py-1.5 text-xs',
    wLine: 'w-96 max-w-96',
    wPlan: 'w-20 max-w-20',
    wDay: 'w-80 max-w-80',
  },
  // ~22px rows — the floor this degrades to; below it a second screen is the answer, not smaller type.
  sm: {
    cell: 'py-0.5 px-2 align-middle',
    header: 'py-1 px-2 text-xs font-semibold tracking-widest uppercase',
    lineId: 'text-base font-bold leading-none',
    mo: 'text-xs',
    planHr: 'text-base font-bold',
    hourHead: 'text-xs',
    hourTrack: 'h-7 rounded-sm',
    ctq: 'text-xs px-1.5 py-0',
    style: 'text-xs',
    hourFigure: 'text-sm',
    dayTrack: 'h-3 rounded-full',
    dayValue: 'w-14 text-base font-bold',
    dayPlan: 'w-14 text-xs',
    footLabel: 'text-xs tracking-widest',
    footFigure: 'text-sm',
    footPct: 'text-xs',
    legend: 'px-2 py-1.5 text-xs',
    wLine: 'w-80 max-w-80',
    wPlan: 'w-20 max-w-20',
    wDay: 'w-72 max-w-72',
  },
  // At a desk the board is a report again, at report density.
  desk: {
    cell: 'py-1.5 px-2 align-middle',
    header: 'py-1.5 px-2 text-xs font-semibold tracking-widest uppercase',
    lineId: 'text-base font-bold leading-none',
    mo: 'text-xs',
    planHr: 'text-base font-bold',
    hourHead: 'text-xs',
    hourTrack: 'h-8 rounded-sm',
    ctq: 'text-xs px-1.5 py-0',
    style: 'text-xs',
    hourFigure: 'text-sm',
    dayTrack: 'h-4 rounded-full',
    dayValue: 'w-14 text-base font-bold',
    dayPlan: 'w-16 text-xs',
    footLabel: 'text-xs tracking-widest',
    footFigure: 'text-sm',
    footPct: 'text-xs',
    legend: 'px-1 py-2 text-xs',
    wLine: 'w-96 max-w-96',
    wPlan: 'w-20 max-w-20',
    wDay: 'w-80 max-w-80',
  },
};

const FABRIC_PREFIX = /^(?:KNIT|WOVEN)\s+/i;

/** Display only — the API and the export keep the full `product_type`, fabric word and all. */
function styleLabel(value: string): string {
  const stripped = value.replace(FABRIC_PREFIX, '').trim();
  // A product_type that is only the fabric word keeps it — an empty cell would be worse.
  return stripped === '' ? value : stripped;
}

/** "07:30 - 08:30" reads as "07:30" on a wall; the full range stays as the column's title. */
function startOf(timeslot: string): string {
  const [start] = timeslot.split('-');
  return start === undefined ? timeslot : start.trim();
}

function hourIsPending(hour: HourRow): boolean {
  return hour.is_elapsed !== 1;
}

function hourIsCurrent(hour: HourRow): boolean {
  return hour.is_current === 1;
}

/**
 * One hour for one line. The bar is the output against that hour's plan; the hairline across it is
 * the plan itself, so "did this line make its hour?" is answered by whether the colour reaches the
 * line — no arithmetic asked of the reader (§4.6).
 */
function HourCell({
  output,
  plan,
  status,
  s,
}: {
  output: number | null;
  plan: number | null;
  status: CellStatus;
  s: Scale;
}) {
  const pending = isPending(status);
  const tone = cellTone(status);
  const value = output ?? 0;
  const planned = plan ?? 0;
  const scale = cellScale(value, planned);

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden',
        s.hourTrack,
        pending ? 'bg-muted/40' : 'bg-muted',
      )}
    >
      {pending ? null : (
        <>
          <div
            className={cn('absolute inset-x-0 bottom-0', tone.fill)}
            style={{ height: heightPct(value, scale) }}
            aria-hidden
          />
          {planned <= 0 ? null : (
            // A reference mark, not a reading. Dashed so it reads as a guide rather than as part
            // of the bar, and quiet enough that the figure above it is what the eye lands on: a
            // solid rule at /75 competed with the number, and at /50 still drew attention.
            <div
              className="absolute inset-x-0 border-t border-dashed border-foreground/35"
              style={{ bottom: heightPct(planned, scale) }}
              aria-hidden
            />
          )}
          <span
            className={cn(
              'absolute inset-x-0 top-0 text-center leading-tight font-bold tabular-nums',
              s.hourFigure,
            )}
          >
            {integer.format(value)}
          </span>
        </>
      )}
      <span className="sr-only">
        {pending ? CELL_STATUS_LABEL.PENDING : `${integer.format(value)} — ${CELL_STATUS_LABEL[status]}`}
      </span>
    </div>
  );
}

/**
 * The day so far. The bar's length is output against the WHOLE day's plan, while the tick is where
 * the line should have reached by now and the figure's colour is the pace it is actually holding —
 * three different questions the reference asks in one control, and all three arrive from SQL.
 */
function DayCell({ row, paceMarkPct, s }: { row: LineRow; paceMarkPct: number | null; s: Scale }) {
  const tone = cellTone(cellStatus(row.pace_status));
  const total = num(row, 'total_output');
  const planDay = num(row, 'planned_quantity_per_day');

  return (
    <div className="flex items-center gap-3">
      <div className={cn('relative min-w-16 flex-1 bg-muted', s.dayTrack)}>
        <div
          className={cn('absolute inset-y-0 left-0', s.dayTrack, tone.fill)}
          style={{ width: widthPct(num(row, 'performance_pct')) }}
          aria-hidden
        />
        {paceMarkPct === null ? null : (
          <div
            className="absolute -inset-y-1 w-0.5 rounded-xs bg-foreground/70"
            style={{ left: widthPct(paceMarkPct) }}
            aria-hidden
          />
        )}
      </div>
      <span className={cn('shrink-0 text-right tabular-nums', s.dayValue, tone.text)}>
        {count(total)}
      </span>
      <span className={cn('shrink-0 text-right text-muted-foreground tabular-nums', s.dayPlan)}>
        {planDay === null ? '' : `/ ${count(planDay)}`}
      </span>
    </div>
  );
}

export interface BoardColumnsInput {
  /** `hours` rows, already ordered — the authority for the hourly column set. */
  hours: readonly HourRow[];
  cells: CellIndex;
  tier: Tier;
  /** Where the day bar's tick sits: the share of the day's hours already finished. */
  paceMarkPct: number | null;
}

export function sewingHourlyBoardColumns({
  hours,
  cells,
  tier,
  paceMarkPct,
}: BoardColumnsInput): ColumnDef<LineRow>[] {
  const s = SCALE[tier];
  const meta = (
    width: string,
    extra?: Partial<{ align: 'left' | 'right'; headerAlign: 'left' | 'right'; exportLabel: string }>,
  ) => ({
    cellClassName: cn(s.cell, width),
    headerClassName: cn(s.header, width, 'text-muted-foreground'),
    ...extra,
  });

  /**
   * One column per timeslot, flat rather than under a group band. A band would add a second header
   * row — 32px of a wall screen's height for a label the hour headings and the legend already give,
   * and one tier of row height is worth more than the words.
   *
   * Not sortable on purpose: these fields are on no `sort:` whitelist, and a header offering an
   * order the export cannot repeat is a screen and a file that disagree. They
   * carry no `exportLabel` either, which keeps `exportColumns` — and so the Excel file — on the
   * declared columns of the `lines` view.
   */
  const hourly: ColumnDef<LineRow>[] = hours.map((hour) => {
      const pending = hourIsPending(hour);
      const current = hourIsCurrent(hour);
      return {
        id: hourColumnId(hour),
        // The heading shows the hour it starts; the full range stays reachable on hover and to a
        // screen reader, which a bare "07:30" would not give. The running hour is named as such
        // rather than only tinted — colour is never the whole signal (§4.6).
        header: () => (
          <span title={current ? `${hour.timeslot} — running now` : hour.timeslot}>
            {startOf(hour.timeslot)}
            {current ? <span className="sr-only"> — hour running now</span> : null}
          </span>
        ),
        enableSorting: false,
        size: 64,
        minSize: 40,
        meta: {
          cellClassName: cn(s.cell, 'px-0.5'),
          headerClassName: cn(
            s.header,
            'px-0.5 text-center normal-case tracking-normal',
            s.hourHead,
            pending ? 'text-muted-foreground/50' : 'text-muted-foreground',
            current && 'rounded-t-sm bg-foreground/10 font-bold text-foreground',
          ),
        },
        cell: ({ row }) => {
          const cell = cells.get(row.original.sfts_line)?.get(hour.timeslot_seq);
          return (
            <HourCell
              s={s}
              output={cell?.output ?? null}
              plan={cell?.planned_quantity_per_hour ?? null}
              status={pending ? 'PENDING' : cellStatus(cell?.cell_status)}
            />
          );
        },
    };
  });

  return [
    {
      id: 'line_name',
      accessorFn: (row) => str(row, 'line_name'),
      header: 'Line · MO',
      enableSorting: true,
      meta: meta(s.wLine, { exportLabel: 'Line' }),
      cell: ({ row }) => {
        const line = row.original;
        return (
          <div className="flex min-w-0 items-baseline gap-2 overflow-hidden whitespace-nowrap">
            {/* Tinted by the pace it is holding, not its share of an unfinished day — see design-notes.md. */}
            <span className={cn('shrink-0', s.lineId, cellTone(cellStatus(line.pace_status)).text)}>
              {str(line, 'line_name')}
            </span>
            {/* Its own token, not a status colour: CTQ is a property of the order, not a verdict on
                the line — the same treatment and the same wording as the floor board. */}
            {line['has_ctq'] === true ? (
              <span
                className={cn(
                  'shrink-0 rounded-sm bg-ctq font-bold tracking-wide text-ctq-foreground uppercase',
                  s.ctq,
                )}
                title="Critical to quality — this line is running at least one CTQ order today"
              >
                CTQ
              </span>
            ) : null}
            <span className={cn('shrink-0', s.style, 'text-muted-foreground')}>
              {styleLabel(str(line, 'product_type'))}
            </span>
            <span
              className={cn(s.mo, 'min-w-0 truncate text-muted-foreground/70')}
              title={str(line, 'planned_batch_numbers')}
            >
              {str(line, 'planned_batch_numbers')}
            </span>
          </div>
        );
      },
    },
    {
      id: 'planned_quantity_per_hour',
      accessorFn: (row) => num(row, 'planned_quantity_per_hour'),
      header: 'Plan/hr',
      enableSorting: true,
      meta: meta(s.wPlan, { align: 'right', headerAlign: 'right', exportLabel: 'Plan/hr' }),
      cell: ({ row }) => (
        <span className={cn('block text-right text-muted-foreground tabular-nums', s.planHr)}>
          {count(num(row.original, 'planned_quantity_per_hour'))}
        </span>
      ),
    },
    ...hourly,
    {
      // `id` must be the field name: DataTable sends column.id as the sort field and the export
      // sends it as `field`, both resolved against the view's declared columns.
      id: 'total_output',
      accessorFn: (row) => num(row, 'total_output'),
      header: 'Day total vs plan',
      enableSorting: true,
      meta: meta(s.wDay, { exportLabel: 'Total output' }),
      cell: ({ row }) => <DayCell row={row.original} paceMarkPct={paceMarkPct} s={s} />,
    },
  ];
}

/** Column ids of the hourly leaves, for placing the `hours`-derived footer figures under them. */
export function hourColumnId(hour: HourRow): string {
  return `ts:${String(hour.timeslot_seq)}`;
}

/** The floor total under one hour: what the whole floor made, and that against the hour's plan. */
export function HourFooter({ hour, tier }: { hour: HourRow; tier: Tier }) {
  const s = SCALE[tier];
  if (hourIsPending(hour)) {
    return <span className={cn('block text-center text-muted-foreground/40', s.footFigure)}>—</span>;
  }
  const tone = cellTone(cellStatus(hour.hour_status));
  return (
    <span className="flex flex-col items-center leading-tight">
      <span className={cn('font-bold tabular-nums', s.footFigure, tone.text)}>
        {count(hour.hourly_total)}
      </span>
      <span className={cn('text-muted-foreground tabular-nums', s.footPct)}>
        {wholePercent(hour.perf_to_plan_hour_pct)}
      </span>
    </span>
  );
}

/** The floor's day figure, in the day column of the totals row. */
export function DayFooter({
  output,
  planDay,
  status,
  tier,
}: {
  output: number | null;
  planDay: number | null;
  status: unknown;
  tier: Tier;
}) {
  const s = SCALE[tier];
  return (
    <span className="flex items-baseline justify-end gap-2 whitespace-nowrap">
      <span
        className={cn('font-bold tabular-nums', s.footFigure, cellTone(cellStatus(status)).text)}
      >
        {count(output)}
      </span>
      <span className={cn('text-muted-foreground tabular-nums', s.footPct)}>
        {planDay === null ? '' : `of ${count(planDay)} day plan`}
      </span>
    </span>
  );
}

export function footerLabelClass(tier: Tier): string {
  return cn('font-semibold text-muted-foreground uppercase', SCALE[tier].footLabel);
}

export interface LegendPage {
  index: number;
  count: number;
  from: number;
  to: number;
  total: number;
}

/**
 * Explains the two marks that are not self-describing, what the four colours mean, and — when the
 * floor is too tall for the screen — which slice of it is currently up.
 *
 * The page position is not decoration. A board showing sixteen of thirty lines with nothing saying
 * so reads as a complete floor, and the fourteen lines off screen look like lines that do not
 * exist (§4.3).
 */
export function BoardLegend({ tier, page }: { tier: Tier; page?: LegendPage }) {
  const s = SCALE[tier];
  return (
    <p
      className={cn(
        'flex flex-none flex-wrap items-center gap-x-8 gap-y-1 text-muted-foreground',
        s.legend,
      )}
    >
      {page === undefined ? null : (
        <span className="flex items-center gap-2 font-semibold text-foreground">
          <span>
            {`Lines ${String(page.from)}–${String(page.to)} of ${String(page.total)}`}
          </span>
          {/* The dots are a second channel for the same fact, not the only one — the count above
              says it in words for a reader who cannot separate the filled dot from the rest. */}
          <span className="flex items-center gap-1" aria-hidden>
            {Array.from({ length: page.count }, (_, dot) => (
              <span
                key={dot}
                className={cn(
                  'size-1.5 rounded-full',
                  dot === page.index ? 'bg-foreground' : 'bg-muted-foreground/40',
                )}
              />
            ))}
          </span>
        </span>
      )}
      <span>Bar height = output against that hour&rsquo;s plan · hairline across the bar = the plan</span>
      <span>Tick on the day bar = where a line should have reached by now</span>
      <span className={STATUS_TONES.good.text}>Green over 95% of plan</span>
      <span className={STATUS_TONES.warn.text}>Amber 70–95%</span>
      <span className={STATUS_TONES.bad.text}>Red under 70%, and nothing recorded</span>
      <span>Empty track = the hour has not started</span>
    </p>
  );
}
